/**
 * Image Helper - WebP Utilities
 * 
 * Helper functions for handling WebP-only image system after migration.
 * All images in the system are now WebP format only.
 */

/**
 * Convert any image path to WebP format
 * @param {string} src - Original image path (may be .jpg, .jpeg, .png, or .webp)
 * @returns {string} WebP image path
 */
export function toWebp(src) {
  if (!src) return '';
  // If already .webp, return as-is
  if (/\.webp$/i.test(src)) return src;
  // Convert .jpeg, .jpg, .png to .webp
  return src.replace(/\.(jpe?g|png)$/i, '.webp');
}

/**
 * Get the cover photo URL for a listing
 * @param {object} listing - Listing object with listPhotos and coverPhoto
 * @param {string} size - Size prefix (default: 'x_medium_')
 * @returns {string} Cover photo URL in WebP format
 */
export function getCoverPhotoUrl(listing, size = 'x_medium_') {
  const placeholder = '/images/placeholder.webp';
  
  if (!listing) return placeholder;
  
  const { coverPhoto, listPhotos } = listing;
  
  if (!listPhotos || listPhotos.length === 0) return placeholder;
  
  // Find the photo matching coverPhoto ID, or use first photo
  let photoName = listPhotos[0]?.name;
  if (coverPhoto) {
    const matched = listPhotos.find(p => p.id === coverPhoto);
    if (matched) photoName = matched.name;
  }
  
  if (!photoName) return placeholder;
  
  // Convert to WebP format
  const webpName = toWebp(photoName);
  return `/images/upload/${size}${webpName}`;
}

/**
 * Get image URL with WebP conversion
 * @param {string} fileName - Original filename from database
 * @param {string} directory - Directory path (default: '/images/upload/')
 * @param {string} size - Size prefix (default: 'x_medium_')
 * @returns {string} Full image URL in WebP format
 */
export function getImageUrl(fileName, directory = '/images/upload/', size = 'x_medium_') {
  if (!fileName) return '/images/placeholder.webp';
  const webpName = toWebp(fileName);
  return `${directory}${size}${webpName}`;
}

export default {
  toWebp,
  getCoverPhotoUrl,
  getImageUrl,
};
